import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Box,
  Typography,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  WhatsApp,
  Check,
  Error as AlertCircle,
  Refresh as RefreshCw,
  Warning,
} from '@mui/icons-material';

interface WhatsAppStatus {
  enabled: boolean;
  configured: boolean;
  processed_messages: number;
  extracted_records: number;
  latest_processing?: string;
  phone_number_id?: string;
  error?: string;
}

interface OverallStats {
  total_messages: number;
  total_records: number | null;
  unique_senders: number;
  avg_records_per_message: number | string | null;
}

interface SourceStats {
  message_source: string;
  total_records: number;
  unique_patients: number;
  unique_doctors: number;
  total_amount: number | string | null;
}

interface WhatsAppStats {
  overall: OverallStats;
  daily: unknown[];
  by_source: SourceStats[];
}

interface WhatsAppMessage {
  id: number;
  message_id: string;
  from_number: string;
  contact_name: string | null;
  message_preview: string | null;
  records_extracted: number;
  processed_date: string;
}

/**
 * Heuristic: does an unparsed message look like it was meant to be an
 * appointment? Used to separate genuine parse failures from greetings and
 * chatter, which are correctly ignored.
 */
export const looksLikeAppointment = (text: string | null): boolean => {
  if (!text) return false;
  const signals = [
    /\bdr[a.\s]/i,
    /\bhospital\b/i,
    /\bpaciente\b/i,
    /\bcirurgi/i,
    /\bprocedimento\b/i,
    /r\$\s*[\d.,]+/i,
    /\b\d{1,2}[./]\s?\d{1,2}[./]\s?\d{2,4}\b/, // date, e.g. 15/12/2025 or 15.12.2025
    /\b\d{1,2}:\d{2}\b/, // time, e.g. 06:00
    /\b\d{3,4}\s?hs\b/i, // time in the "1200HS" shorthand the clinic also uses
  ];
  return signals.filter((re) => re.test(text)).length >= 2;
};

const formatDateTime = (value?: string): string =>
  value ? new Date(value).toLocaleString() : '—';

const formatCurrency = (value: number | string | null): string => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'BRL' });
};

const WhatsAppMessages: React.FC = () => {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [stats, setStats] = useState<WhatsAppStats | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [statusRes, statsRes, msgRes] = await Promise.all([
        fetch('/api/whatsapp/status'),
        fetch('/api/whatsapp/stats'),
        fetch('/api/whatsapp/messages?limit=100'),
      ]);

      // 503 means the backend was started without WhatsApp configured.
      if ([statusRes, statsRes, msgRes].some((r) => r.status === 503)) {
        setDisabled(true);
        return;
      }

      if (statusRes.ok) setStatus(await statusRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (msgRes.ok) setMessages(await msgRes.json());

      if (!statusRes.ok && !statsRes.ok && !msgRes.ok) {
        setError('Could not load WhatsApp data from the server.');
      }
    } catch (err) {
      setError('Network error while loading WhatsApp data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (disabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" icon={<WhatsApp />}>
          <AlertTitle>WhatsApp integration is not enabled</AlertTitle>
          The backend is running without WhatsApp credentials. Set the
          <code> WHATSAPP_* </code> variables in <code>.env</code> and restart
          <code> meddata-backend </code> to enable this page.
        </Alert>
      </Box>
    );
  }

  const overall = stats?.overall;
  const totalRecords = overall?.total_records ?? 0;
  const totalMessages = overall?.total_messages ?? 0;
  const extractionRate =
    totalMessages > 0 ? Math.round((totalRecords / totalMessages) * 100) : 0;

  // Messages that produced nothing but look like real appointment data are
  // the actionable ones: they are silent data loss.
  const unparsed = messages.filter(
    (m) => m.records_extracted === 0 && looksLikeAppointment(m.message_preview)
  );

  const summaryTiles = [
    { label: 'Messages received', value: totalMessages },
    { label: 'Records extracted', value: totalRecords },
    { label: 'Extraction rate', value: `${extractionRate}%` },
    { label: 'Unique senders', value: overall?.unique_senders ?? 0 },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" component="h1">
          WhatsApp
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshCw />}
          onClick={loadAll}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Connection status */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<WhatsApp color="success" />}
          title="Connection"
          subheader={
            status?.phone_number_id
              ? `Phone number ID ${status.phone_number_id}`
              : undefined
          }
          action={
            <Chip
              icon={status?.configured ? <Check /> : <AlertCircle />}
              label={status?.configured ? 'Configured' : 'Not configured'}
              color={status?.configured ? 'success' : 'error'}
              sx={{ mt: 1, mr: 1 }}
            />
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Last message processed: {formatDateTime(status?.latest_processing)}
          </Typography>
        </CardContent>
      </Card>

      {/* Summary tiles */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
          mb: 3,
        }}
      >
        {summaryTiles.map((tile) => (
          <Card key={tile.label}>
            <CardContent>
              <Typography variant="h4">{tile.value}</Typography>
              <Typography variant="body2" color="text.secondary">
                {tile.label}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Unparsed appointment warning */}
      {unparsed.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
          <AlertTitle>
            {unparsed.length} message{unparsed.length === 1 ? '' : 's'} look like
            appointments but produced no record
          </AlertTitle>
          These were received but never written to the database. They are
          highlighted in the table below.
        </Alert>
      )}

      {/* Channel comparison */}
      {stats?.by_source && stats.by_source.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="Records by channel" />
          <CardContent sx={{ pt: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Channel</TableCell>
                    <TableCell align="right">Records</TableCell>
                    <TableCell align="right">Patients</TableCell>
                    <TableCell align="right">Doctors</TableCell>
                    <TableCell align="right">Total value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.by_source.map((s) => (
                    <TableRow key={s.message_source}>
                      <TableCell sx={{ textTransform: 'capitalize' }}>
                        {s.message_source}
                      </TableCell>
                      <TableCell align="right">{s.total_records}</TableCell>
                      <TableCell align="right">{s.unique_patients}</TableCell>
                      <TableCell align="right">{s.unique_doctors}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(s.total_amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Message log */}
      <Card>
        <CardHeader title={`Messages (${messages.length})`} />
        <CardContent sx={{ pt: 0 }}>
          {messages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No WhatsApp messages have been received yet.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Received</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell align="right">Records</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messages.map((m) => {
                    const missed =
                      m.records_extracted === 0 &&
                      looksLikeAppointment(m.message_preview);
                    return (
                      <TableRow
                        key={m.id}
                        sx={missed ? { bgcolor: 'warning.light' } : undefined}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(m.processed_date)}
                        </TableCell>
                        <TableCell>
                          {m.contact_name || '—'}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {m.from_number}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 420,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {m.message_preview || '—'}
                        </TableCell>
                        <TableCell align="right">
                          {missed ? (
                            <Tooltip title="Looks like an appointment but nothing was extracted">
                              <Chip
                                size="small"
                                color="warning"
                                icon={<Warning />}
                                label="missed"
                              />
                            </Tooltip>
                          ) : (
                            <Chip
                              size="small"
                              color={m.records_extracted > 0 ? 'success' : 'default'}
                              label={m.records_extracted}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default WhatsAppMessages;
